import { useCallback, useState } from 'react'

/**
 * Measures an element's height and keeps it live as content and width change.
 *
 * Exists so a sticky element can publish its own height to whatever pins
 * below it. A hard-coded token would be right in exactly one state and wrong
 * in the rest — controls wrap at narrow widths, and applied-filter chips will
 * add a second line — which shows up as day headings pinned *into* the strip
 * above them rather than under it.
 *
 * A callback ref rather than an effect over a RefObject, deliberately: the
 * measured element usually mounts later than its owner (the control strip
 * appears only once the board has loaded), and an effect keyed on the ref
 * object never re-runs to notice that. It fires once against null and reports
 * 0 forever. A callback ref is called by React at attach time, whenever that
 * happens, and its cleanup (React 19) disconnects the observer on detach.
 *
 * Height is 0 until measured, and stays 0 under jsdom where every offset
 * reads zero, so callers must treat 0 as "no offset yet", never as an error.
 */
export function useMeasuredHeight(): {
  ref: (node: HTMLElement | null) => void
  height: number
} {
  const [height, setHeight] = useState(0)

  const ref = useCallback((node: HTMLElement | null) => {
    // Null arrives only on the legacy detach path; with a cleanup returned
    // below, React 19 calls that instead.
    if (!node) return

    // One reading up front: the only one anywhere ResizeObserver is missing,
    // and elsewhere it saves a frame of wrong offset.
    setHeight(node.offsetHeight)
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => setHeight(node.offsetHeight))
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return { ref, height }
}
